import React, { useState, useMemo } from "react";
import axios from "axios";
import { Draggable } from "@hello-pangea/dnd";
import { GripVertical, EyeOff, Eye, Music, ExternalLink, FileAudio, Check, Edit2, Trash2, Plus, Play, Square, Loader2, RefreshCw, Download, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { BUCKETS, BUCKET_LABELS, BUCKET_DEFAULT_MOMENTO, guessBucketFromPeticion } from "../utils/momentoBuckets";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import YouTubeCandidatesDialog from "./YouTubeCandidatesDialog";
import PeticionAudioProgress from "./PeticionAudioProgress";
import { PeticionEditDialog } from "./PeticionEditDialog";
import { getAudioFromCache } from "../utils/audioCache";
import { _detectAudioMime } from "../utils/audioCache";
import { openExternalLink } from "../utils/openExternalLink";
import { haptic } from "../utils/haptic";
import { takeCueIntent } from "../utils/cueIntents";
import { resolveSpotifyUrl } from "../utils/spotifyUrl";
import { API_URL } from "../config";
import logger from "@/utils/logger";

const API = API_URL;

// Componente individual de petición arrastrable
// Extraído de App.js (Fase 1 del refactor — iter Feb 2026)
const SortablePeticionItem = ({ 
  peticion, 
  index, 
  onRemove,
  onToggleHidden,
  playlistId,
  onPlaylistRefresh,
  peticionesPendingFlags, 
  setPeticionesPendingFlags, 
  songs, 
  createSong, 
  playingAudioId, 
  setPlayingAudioId,
  audioStates = {},
  setAudioStates = () => {},
  audioProgress = {},
  setAudioProgress = () => {},
  preloadAudio = async () => {},
  cachedAudios = {} // Nuevo: indica qué audios están en caché
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [ytCandidatesOpen, setYtCandidatesOpen] = useState(false);
  const [ytChangeVersionOpen, setYtChangeVersionOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Feature A — Detector "solo Spotify": petición tiene spotify_uri/id pero NO está
  // en la biblioteca local (Denon). Si ya tiene MP3 propio o YouTube, no marcamos.
  const isSpotifyOnly = useMemo(() => {
    if (!peticion?.spotify_uri && !peticion?.spotify_id) return false;
    if (peticion.audio_file_id || peticion.youtube_url) return false;
    if (!songs || songs.length === 0) return false;
    const spId = peticion.spotify_id || (peticion.spotify_uri || '').split(':').pop();
    const norm = (s) => (s || '').toLowerCase().trim();
    const pTitle = norm(peticion.title || peticion.titulo);
    const pArtist = norm(peticion.artist || peticion.artista);
    return !songs.some((s) => {
      if (spId && (s.spotify_id === spId || s.spotify_uri === peticion.spotify_uri)) return true;
      return norm(s.title) === pTitle && norm(s.artist) === pArtist;
    });
  }, [peticion, songs]);

  // Feb-2026 — Vínculo Peticiones ↔ Biblioteca: si la petición matchea una
  // canción de biblioteca (por spotify_id preferentemente, fallback a
  // título+artista), exponemos su metadata enriquecida (BPM, energía, género)
  // para mostrar chips en la UI y evitar duplicados al promover a biblioteca.
  const linkedSong = useMemo(() => {
    if (!songs || songs.length === 0) return null;
    const spId = peticion.spotify_id || (peticion.spotify_uri || '').split(':').pop();
    const norm = (s) => (s || '').toLowerCase().trim();
    const pTitle = norm(peticion.title || peticion.titulo);
    const pArtist = norm(peticion.artist || peticion.artista);
    return songs.find((s) => {
      if (spId && (s.spotify_id === spId || s.spotify_uri === peticion.spotify_uri)) return true;
      if (!pTitle) return false;
      return norm(s.title) === pTitle && norm(s.artist) === pArtist;
    }) || null;
  }, [peticion, songs]);

  return (
    <>
    <Draggable draggableId={`peticion-${peticion.id || `idx-${index}`}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`bg-slate-900/50 p-4 rounded-lg ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-pink-500' : ''
          } ${peticion.hidden_from_client ? 'border-l-4 border-amber-500' : ''}`}
        >
          <div className="flex items-start gap-3">
            {/* Drag handle — hit-area 44×44 invisible (fat-finger safe) */}
            <div
              {...provided.dragHandleProps}
              className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 min-h-11 min-w-11 flex items-center justify-center -m-2"
              title="Arrastra para reordenar"
            >
              <GripVertical className="w-4 h-4" />
            </div>

        <div className="flex-1 min-w-0 overflow-hidden">
          {/* Indicador de oculto */}
          {peticion.hidden_from_client && (
            <div className="flex items-center gap-1 text-amber-400 text-xs mb-2">
              <EyeOff className="w-3 h-3" />
              <span>Oculta para cliente (sorpresa)</span>
            </div>
          )}
          <div className="flex items-center gap-2 mb-1 min-w-0">
            {peticion.audio_file_id && !peticion.is_custom_upload && (peticion.source === 'youtube' || peticion.source === 'youtube_link' || peticion.tipo === 'youtube_link') ? (
              <div className="flex items-center gap-1 flex-shrink-0">
                <FileAudio className="w-4 h-4 text-red-400" />
                <span className="text-xs bg-red-900/30 text-red-300 px-1.5 py-0.5 rounded whitespace-nowrap">
                  🎵 YT
                </span>
                {cachedAudios[peticion.audio_file_id] && (
                  <span className="text-xs bg-green-900/30 text-green-400 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <Check className="w-3 h-3" />
                  </span>
                )}
              </div>
            ) : (peticion.tipo === 'youtube_link' || peticion.source === 'youtube_link') ? (
              <div className="flex items-center gap-1 flex-shrink-0">
                <FileAudio className="w-4 h-4 text-red-400" />
                <span className="text-xs bg-red-900/30 text-red-300 px-1.5 py-0.5 rounded whitespace-nowrap">
                  🔗 YT
                </span>
              </div>
            ) : peticion.is_custom_upload ? (
              <div className="flex items-center gap-1 flex-shrink-0">
                <FileAudio className="w-4 h-4 text-purple-400" />
                <span className="text-xs bg-purple-900/30 text-purple-300 px-1.5 py-0.5 rounded">
                  {peticion.titulo?.toLowerCase().endsWith('.m4a') ? 'M4A' : 'MP3'}
                </span>
                {cachedAudios[peticion.audio_file_id] && (
                  <span className="text-xs bg-green-900/30 text-green-400 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <Check className="w-3 h-3" />
                  </span>
                )}
              </div>
            ) : (
              <Music className="w-4 h-4 text-pink-400 flex-shrink-0" />
            )}
            <h4 className="text-white font-semibold truncate flex-1 min-w-0" title={peticion.title || peticion.titulo}>{peticion.title || peticion.titulo}</h4>
            {isSpotifyOnly && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-300 border border-emerald-700/50 flex-shrink-0"
                title="No está en tu biblioteca Denon — usa Spotify"
                data-testid={`spotify-only-badge-${peticion.id}`}
              >
                📦 SOLO SPOTIFY
              </span>
            )}
          </div>
          <p className="text-slate-400 text-sm truncate">{peticion.artist || peticion.artista}</p>
          {/* Feb-2026 — Vínculo con biblioteca: si matchea una canción
              (por spotify_id o título+artista), mostrar BPM/energía/género
              heredados para que el DJ sepa que está respaldada en Denon. */}
          {linkedSong && (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-cyan-900/40 text-cyan-300 border border-cyan-700/50"
                title="Esta canción ya está en tu biblioteca"
                data-testid={`in-library-badge-${peticion.id}`}
              >
                📚 En biblioteca
              </span>
              {linkedSong.bpm && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200" title="BPM de biblioteca">
                  {linkedSong.bpm} BPM
                </span>
              )}
              {linkedSong.energia && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200" title="Energía">
                  {linkedSong.energia}
                </span>
              )}
              {linkedSong.genres && linkedSong.genres[0] && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200" title="Género principal">
                  {linkedSong.genres[0]}
                </span>
              )}
            </div>
          )}
          {peticion.momento && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-900/40 border border-amber-600/50 text-amber-300 text-[11px] font-medium"
                title={`Momento del evento: ${peticion.momento}`}
                data-testid={`peticion-momento-badge-${peticion.id}`}
              >
                <span aria-hidden="true">🎯</span>
                {peticion.momento}
              </span>
              {/* "Mover a…" — cambia el momento a otro bucket (útil para reclasificar) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400 hover:text-cyan-300 hover:border-cyan-600/50 text-[10px] font-medium"
                    title="Mover a otro momento"
                    data-testid={`move-momento-btn-${peticion.id}`}
                  >
                    <ArrowRightLeft className="w-3 h-3" />
                    Mover
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="bg-slate-900 border-slate-700 text-slate-200 w-48"
                >
                  <DropdownMenuLabel className="text-xs text-slate-500">
                    Mover a momento
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  {BUCKETS.map((b) => {
                    const current = guessBucketFromPeticion(peticion);
                    const isCurrent = b === current;
                    return (
                      <DropdownMenuItem
                        key={b}
                        disabled={isCurrent}
                        onClick={async () => {
                          if (isCurrent) return;
                          try {
                            const token = localStorage.getItem('token');
                            await axios.patch(
                              `${API}/playlists/${playlistId}/peticiones/${peticion.id}`,
                              { momento: BUCKET_DEFAULT_MOMENTO[b] },
                              { headers: { Authorization: `Bearer ${token}` } },
                            );
                            toast.success(`Movida a ${BUCKET_LABELS[b]}`);
                            if (onPlaylistRefresh) onPlaylistRefresh();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Error moviendo petición');
                          }
                        }}
                        className={`text-xs cursor-pointer ${isCurrent ? 'opacity-50' : ''}`}
                        data-testid={`move-to-${b}-${peticion.id}`}
                      >
                        {BUCKET_LABELS[b]}{isCurrent ? ' (actual)' : ''}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          {/* Sin momento asignado: dropdown "Asignar momento" */}
          {!peticion.momento && (
            <div className="mt-1.5">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-800 border border-slate-600 text-slate-400 hover:text-cyan-300 hover:border-cyan-600/50 text-[10px] font-medium"
                    title="Asignar momento"
                    data-testid={`assign-momento-btn-${peticion.id}`}
                  >
                    <ArrowRightLeft className="w-3 h-3" />
                    Asignar momento
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-slate-900 border-slate-700 text-slate-200 w-48">
                  <DropdownMenuLabel className="text-xs text-slate-500">Asignar a momento</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  {BUCKETS.map((b) => (
                    <DropdownMenuItem
                      key={b}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.patch(
                            `${API}/playlists/${playlistId}/peticiones/${peticion.id}`,
                            { momento: BUCKET_DEFAULT_MOMENTO[b] },
                            { headers: { Authorization: `Bearer ${token}` } },
                          );
                          toast.success(`Asignada a ${BUCKET_LABELS[b]}`);
                          if (onPlaylistRefresh) onPlaylistRefresh();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Error asignando momento');
                        }
                      }}
                      className="text-xs cursor-pointer"
                    >
                      {BUCKET_LABELS[b]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          {peticion.is_custom_upload && peticion.file_size_mb && (
            <p className="text-slate-500 text-xs mt-1">Tamaño: {peticion.file_size_mb} MB</p>
          )}
          {peticion.nota && (
            <div className="mt-2 p-2 bg-slate-800 rounded overflow-hidden">
              <p className="text-slate-300 text-sm break-words">
                📝 {peticion.nota}
              </p>
            </div>
          )}
          
          {/* Botones de acción */}
          <div className="flex gap-1.5 sm:gap-2 mt-3 flex-wrap">
            {/* Botón para YouTube Link */}
            {(peticion.tipo === 'youtube_link' || peticion.source === 'youtube_link' || peticion.source === 'youtube') && peticion.youtube_url && (
              <a
                href={peticion.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md text-xs sm:text-sm font-medium transition-colors border border-red-700/50 bg-red-900/20 hover:bg-red-900/40 text-red-300 h-8 sm:h-9 px-2 sm:px-3"
                data-testid={`youtube-link-btn-${peticion.id}`}
              >
                <ExternalLink className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Ver en YouTube</span>
              </a>
            )}
            
            {/* Botón para Spotify Link (peticiones desde email_ai o portal) */}
            {(peticion.tipo === 'spotify_link' || peticion.source === 'spotify_link') && peticion.spotify_url && (
              <a
                href={peticion.spotify_url}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openExternalLink(peticion.spotify_url);
                }}
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md text-xs sm:text-sm font-medium transition-colors border border-green-700/50 bg-green-900/20 hover:bg-green-900/40 text-green-300 h-8 sm:h-9 px-2 sm:px-3"
                data-testid={`spotify-link-btn-${peticion.id}`}
              >
                <ExternalLink className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Abrir en Spotify</span>
              </a>
            )}
            
            {/* Botones para archivos MP3 (subidos o de YouTube).
                Basta con tener audio_file_id (MP3 descargado/subido): así también
                aparecen para peticiones cuyo `source` quedó como 'youtube_link'
                tras una re-detección del documento (conservando el audio). Es
                coherente con la barra de progreso de abajo, que ya depende solo
                de audio_file_id. */}
            {peticion.audio_file_id && (
              <>
                <Button
                  onClick={async (e) => {
                    e.preventDefault();
                    haptic(10);
                    
                    const audioFileId = peticion.audio_file_id;
                    const audioId = `audio-${audioFileId}`;
                    const currentState = audioStates[audioFileId];
                    
                    // Detectar móvil/PWA
                    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                    const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
                    
                    // Si está cargando, no hacer nada
                    if (currentState === 'loading') {
                      logger.log('⏳ Audio aún cargando...');
                      return;
                    }
                    
                    let audio = document.getElementById(audioId);
                    
                    // Validar si el audio existe Y es válido
                    let needsReload = false;
                    
                    if (!audio) {
                      logger.log('🎵 Audio no existe, necesita carga inicial');
                      needsReload = true;
                    } else {
                      // El audio existe, pero verificar si su src es válido
                      const hasSrc = audio.src && audio.src !== '';
                      const isBlobUrl = audio.src.startsWith('blob:');
                      
                      logger.log(`🔍 Verificando audio existente:`);
                      logger.log(`   - Tiene src: ${hasSrc}`);
                      logger.log(`   - Es Blob URL: ${isBlobUrl}`);
                      logger.log(`   - readyState: ${audio.readyState}`);
                      logger.log(`   - networkState: ${audio.networkState}`);
                      logger.log(`   - error: ${audio.error ? audio.error.code : 'ninguno'}`);
                      
                      // Si tiene error O no tiene src válido O readyState es 0, necesita recarga
                      if (audio.error || !hasSrc || audio.readyState === 0 || audio.networkState === 3) {
                        logger.log('⚠️ Audio existe pero está en estado inválido, recargando...');
                        // Limpiar el audio viejo
                        audio.pause();
                        if (isBlobUrl) {
                          URL.revokeObjectURL(audio.src);
                        }
                        audio.remove();
                        needsReload = true;
                      }
                    }
                    
                    // Si no existe o está en error o inválido, precargar
                    if (needsReload || currentState === 'error' || currentState === 'not-loaded') {
                      logger.log('🔄 Precargando audio...');
                      await preloadAudio(audioFileId);
                      audio = document.getElementById(audioId);
                      
                      if (!audio) {
                        console.error('❌ No se pudo cargar el audio');
                        return;
                      }
                    }
                    
                    // Si el audio no existe, crearlo con Blob URL (fallback)
                    if (!audio) {
                      logger.log('🎵 Creando elemento audio...');
                      
                      const token = localStorage.getItem('token');
                      
                      // Función para cargar audio con reintentos
                      const downloadWithRetry = async (attempt = 1) => {
                        logger.log(`🔄 Descargando audio (intento ${attempt})...`);
                        
                        const controller = new AbortController();
                        const timeoutId = setTimeout(() => controller.abort(), 30000);
                        
                        const response = await fetch(`${API}/audio/${peticion.audio_file_id}`, {
                          method: 'GET',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'audio/mpeg, audio/mp4, audio/*;q=0.9, */*;q=0.5',
                            'Cache-Control': 'no-cache'
                          },
                          signal: controller.signal,
                          cache: 'no-store'
                        });
                        
                        clearTimeout(timeoutId);
                        
                        if (!response.ok) {
                          if (response.status === 401) {
                            alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
                            localStorage.removeItem('token');
                            window.location.href = '/';
                            return null;
                          } else if (response.status === 404) {
                            alert('Este archivo de audio ya no existe en el servidor.');
                            return null;
                          } else if (response.status === 403) {
                            alert('No tienes permiso para acceder a este archivo.');
                            return null;
                          } else {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                          }
                        }
                        
                        // Usar arrayBuffer para mayor compatibilidad con iOS
                        const arrayBuffer = await response.arrayBuffer();
                        if (arrayBuffer.byteLength < 1024) {
                          throw new Error(`respuesta muy pequeña (${arrayBuffer.byteLength} bytes) — el servidor pudo devolver un error HTML`);
                        }
                        // feb-2026 (bug iPad Air 5): detectar MIME real desde magic bytes
                        // en vez de fiarse del header; algunos Safari iOS rechazan blobs
                        // con MIME incorrecto con MEDIA_ERR_SRC_NOT_SUPPORTED.
                        const headerType = response.headers.get('content-type') || '';
                        const detectedType = _detectAudioMime(arrayBuffer) || (
                          headerType.startsWith('audio/') ? headerType : 'audio/mpeg'
                        );
                        logger.log(`🎵 MIME detectado: ${detectedType} (header: ${headerType || 'ninguno'})`);
                        const blob = new Blob([arrayBuffer], { type: detectedType });
                        return URL.createObjectURL(blob);
                      };
                      
                      try {
                        let blobUrl;
                        try {
                          blobUrl = await downloadWithRetry(1);
                        } catch (error) {
                          if (error.message && error.message.includes('disturbed')) {
                            console.warn('⚠️ Error de body disturbed, reintentando...');
                            await new Promise(r => setTimeout(r, 300));
                            blobUrl = await downloadWithRetry(2);
                          } else {
                            throw error;
                          }
                        }
                        
                        if (!blobUrl) return; // Usuario ya fue alertado
                        
                        logger.log('✅ Blob URL creado');
                        
                        // Detectar si es PWA/móvil para optimizar memoria
                        const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                        const isPWAMode = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
                        
                        logger.log(`📱 Modo PWA/Móvil detectado: ${isPWAMode || isMobileDevice}`);
                        
                        // OPTIMIZACIÓN PWA: Limpiar audios antiguos ANTES de crear el nuevo
                        if (isMobileDevice || isPWAMode) {
                          document.querySelectorAll('audio').forEach(a => {
                            // Solo mantener el que está reproduciendo actualmente
                            if (a.id !== `audio-${playingAudioId}` && a.id !== audioId) {
                              logger.log(`🗑️ Limpiando audio ${a.id} antes de cargar nuevo`);
                              a.pause();
                              if (a.src.startsWith('blob:')) {
                                URL.revokeObjectURL(a.src);
                              }
                              a.remove();
                            }
                          });
                        }
                        
                        // Crear elemento audio con Blob URL
                        audio = document.createElement('audio');
                        audio.id = audioId;
                        // IMPORTANTE: usar 'auto' para que se precargue bien, pero limitamos cantidad de audios
                        audio.preload = 'auto';
                        audio.style.display = 'none';
                        
                        // Flag para saber si el audio está listo
                        audio.dataset.isReady = 'false';
                        audio.dataset.hasError = 'false';
                        
                        // Listeners
                        audio.addEventListener('loadedmetadata', () => {
                          logger.log('✅ Audio metadata cargada');
                          audio.dataset.isReady = 'true';
                        });
                        
                        audio.addEventListener('canplay', () => {
                          logger.log('✅ Audio listo para reproducir');
                          audio.dataset.isReady = 'true';
                        });
                        
                        // Listener para detectar buffering (cuando se queda sin datos)
                        audio.addEventListener('waiting', () => {
                          logger.log('⏸️ Buffering... (esperando más datos)');
                        });
                        
                        audio.addEventListener('playing', () => {
                          logger.log('▶️ Reproduciendo (buffering completado)');
                        });
                        
                        audio.addEventListener('pause', () => {
                          setPlayingAudioId(null);
                        });
                        
                        audio.addEventListener('ended', () => {
                          setPlayingAudioId(null);
                        });
                        
                        audio.addEventListener('error', (e) => {
                          console.error('❌ Audio error:', audio.error);
                          setPlayingAudioId(null);
                          audio.dataset.hasError = 'true';
                          
                          // Mostrar error específico solo en casos graves
                          if (audio.error) {
                            // Añadir diagnóstico completo al log (útil para bugs device-specific
                            // como el reportado en iPad Air 5): tipo MIME, tamaño, readyState, etc.
                            console.error('🩺 Diagnóstico audio:', {
                              errorCode: audio.error.code,
                              errorMessage: audio.error.message,
                              srcType: audio.src.substring(0, 60),
                              readyState: audio.readyState,
                              networkState: audio.networkState,
                              currentSrcMime: audio.currentSrc ? 'set' : 'empty',
                              userAgent: navigator.userAgent.substring(0, 120),
                            });
                            if (audio.error.code === 4) {
                              // Error 4 = MEDIA_ERR_SRC_NOT_SUPPORTED. Puede ser 404 O formato
                              // no reconocido por el navegador (iPad Air 5, versiones antiguas
                              // de Safari). Ahora explicamos ambas causas al usuario.
                              console.error('❌ Error 4: MEDIA_ERR_SRC_NOT_SUPPORTED');
                              alert('⚠️ No se pudo cargar el audio.\n\nEsto suele significar:\n• Este dispositivo/Safari no puede decodificar el formato\n• O el archivo ya no existe en el servidor\n\nProbar en otro dispositivo o eliminar la petición y volver a subirla. Detalles técnicos en la consola.');
                            } else if (audio.error.code === 3) {
                              // Error 3 = MEDIA_ERR_DECODE (archivo corrupto)
                              console.error('❌ Error 3: Error al decodificar audio');
                              alert('⚠️ Error al decodificar el audio. El archivo puede estar corrupto.');
                            }
                            // Para otros errores, solo log en consola, no molestar al usuario
                            // El siguiente click intentará recrear el audio
                          }
                        });
                        
                        document.body.appendChild(audio);
                        
                        // Asignar src DESPUÉS de agregar listeners
                        audio.src = blobUrl;
                        
                        logger.log('✅ Audio element creado con Blob URL');
                        
                      } catch (error) {
                        console.error('❌ Error al cargar audio:', error);
                        // SEGURIDAD: se eliminó el fallback que exponía el token en la URL.
                        // Si falla la carga blob, pedimos al usuario que reintente.
                        if (error.message && error.message.includes('disturbed')) {
                          console.warn('⚠️ Body disturbed — pidiendo reintento');
                          alert('⚠️ Hubo un problema al cargar el audio. Pulsa play de nuevo para reintentar.');
                        } else {
                          alert(`Error al cargar el audio: ${error.message}`);
                        }
                        return;
                      }
                    }
                    
                    // Reproducir o pausar
                    try {
                      if (audio.paused) {
                        logger.log('▶️ Intentando reproducir...');
                        logger.log(`📊 Estado del audio - readyState: ${audio.readyState}, src: ${audio.src ? 'OK' : 'FALTA'}`);
                        
                        // Verificar si el audio está listo
                        // readyState >= 2 (HAVE_CURRENT_DATA) es necesario para empezar a reproducir en móvil
                        if (audio.readyState < 2) {
                          logger.log('⏳ Esperando que el audio tenga suficientes datos...');
                          logger.log(`   readyState actual: ${audio.readyState} (necesitamos >= 2)`);
                          
                          // Mostrar feedback al usuario
                          setPlayingAudioId(peticion.audio_file_id);
                          
                          // Esperar a que tenga al menos HAVE_CURRENT_DATA (readyState >= 2)
                          const waitForReady = new Promise((resolve, reject) => {
                            const timeout = setTimeout(() => {
                              console.error(`❌ Timeout - readyState final: ${audio.readyState}`);
                              reject(new Error(`Timeout (readyState=${audio.readyState})`));
                            }, 30000); // Iter91.6: 30s para iOS PWA en redes lentas (antes 15s)
                            
                            const checkReady = () => {
                              logger.log(`   🔄 Verificando... readyState: ${audio.readyState}`);
                              if (audio.readyState >= 2) {
                                clearTimeout(timeout);
                                logger.log('✅ Audio tiene suficientes datos');
                                resolve();
                              } else {
                                setTimeout(checkReady, 100); // Revisar cada 100ms
                              }
                            };
                            
                            // También escuchar eventos que indican que está listo
                            audio.addEventListener('canplay', () => {
                              clearTimeout(timeout);
                              logger.log('✅ Audio listo (evento canplay)');
                              resolve();
                            }, { once: true });
                            
                            audio.addEventListener('loadeddata', () => {
                              if (audio.readyState >= 2) {
                                clearTimeout(timeout);
                                logger.log('✅ Audio listo (evento loadeddata)');
                                resolve();
                              }
                            }, { once: true });
                            
                            // Iniciar verificación
                            checkReady();
                          });
                          
                          try {
                            await waitForReady;
                            logger.log('✅ Audio preparado, iniciando reproducción');
                          } catch (waitError) {
                            console.error('❌ Timeout esperando audio:', waitError);
                            setPlayingAudioId(null);
                            // Iter91.6: toast no-bloqueante en lugar de alert() modal
                            toast.error('El audio está tardando mucho. Comprueba conexión y vuelve a pulsar Reintentar.', { duration: 6000 });
                            return;
                          }
                        }
                        
                        logger.log('▶️ Reproduciendo...');
                        
                        // Pausar todos los otros audios
                        document.querySelectorAll('audio').forEach(a => {
                          if (a.id !== audioId) {
                            a.pause();
                          }
                        });
                        
                        // FIX: En iOS/PWA, forzar load() antes de play() para audios desde caché
                        // Esto reinicializa el contexto de audio del navegador
                        if ((isMobile || isPWA) && audio.src.startsWith('blob:')) {
                          logger.log('🔄 Forzando reload del audio desde caché (fix iOS)...');
                          audio.load();
                          
                          // Esperar un momento a que el load() inicialice
                          await new Promise(resolve => setTimeout(resolve, 100));
                        }
                        
                        audio.volume = 1.0;
                        
                        // Si hay una intención de CUE pendiente, aplicar el seek ANTES de play()
                        // para que la reproducción arranque directamente en la posición del CUE
                        // (evita el "flash" desde 0:00 al hacer tap en un CUE con audio no cargado)
                        const cueStartAt = takeCueIntent(peticion.audio_file_id);
                        if (cueStartAt !== undefined && cueStartAt > 0) {
                          try {
                            audio.currentTime = Math.max(0, Math.min(cueStartAt, audio.duration || cueStartAt));
                            logger.log(`🎯 CUE intent aplicado: seek a ${cueStartAt.toFixed(2)}s antes de play()`);
                          } catch (seekErr) {
                            console.warn('⚠️ Error aplicando CUE intent:', seekErr);
                          }
                        }
                        
                        // Reproducir
                        const playPromise = audio.play();
                        
                        if (playPromise !== undefined) {
                          playPromise
                            .then(() => {
                              setPlayingAudioId(peticion.audio_file_id);
                              logger.log('✅ Reproducción iniciada');
                              
                              // OPTIMIZACIÓN: Después del primer play exitoso, cambiar a preload='auto'
                              // para que el navegador descargue el resto en background
                              if (audio.dataset.hasPlayedOnce !== 'true') {
                                audio.dataset.hasPlayedOnce = 'true';
                                audio.preload = 'auto'; // Ahora sí, descargar todo en background
                                logger.log('📥 Precargando audio completo en background...');
                              }
                            })
                            .catch(err => {
                              console.error('❌ Error al reproducir:', err);
                              console.error('   Tipo de error:', err.name);
                              console.error('   Estado del audio:', {
                                readyState: audio.readyState,
                                networkState: audio.networkState,
                                paused: audio.paused,
                                src: audio.src.substring(0, 50) + '...'
                              });
                              
                              // CRÍTICO: Resetear el estado visual
                              setPlayingAudioId(null);
                              
                              if (err.name === 'NotAllowedError') {
                                alert('⚠️ El navegador bloqueó la reproducción automática.\n\nHaz click en el botón de nuevo para reproducir.');
                              } else if (err.name === 'NotSupportedError') {
                                alert('⚠️ Formato de audio no compatible con este navegador.');
                              } else if (err.name === 'AbortError') {
                                logger.log('ℹ️ Reproducción abortada (probablemente el usuario pausó)');
                              } else {
                                alert(`Error al reproducir: ${err.message}`);
                              }
                            });
                        }
                      } else {
                        logger.log('⏹ Deteniendo...');
                        audio.pause();
                        audio.currentTime = 0; // ✅ Reiniciar al inicio
                        setPlayingAudioId(null);
                      }
                    } catch (err) {
                      console.error('❌ Error:', err);
                      setPlayingAudioId(null);
                    }
                  }}
                  size="sm"
                  className={
                    "h-11 px-4 " + (
                      playingAudioId === peticion.audio_file_id 
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : audioStates[peticion.audio_file_id] === 'loading'
                          ? 'bg-blue-600 cursor-wait text-white'
                          : audioStates[peticion.audio_file_id] === 'error'
                            ? 'bg-orange-600 hover:bg-orange-700 text-white'
                            : audioStates[peticion.audio_file_id] === 'ready'
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-slate-600 hover:bg-slate-700 text-white'
                    )
                  }
                  data-testid={`play-audio-button-${peticion.id || peticion.audio_file_id}`}
                  disabled={audioStates[peticion.audio_file_id] === 'loading'}
                  title={playingAudioId === peticion.audio_file_id ? "Detener" : "Reproducir"}
                >
                  {playingAudioId === peticion.audio_file_id ? (
                    <>
                      <Square className="w-3 h-3 mr-1" />
                      Detener
                    </>
                  ) : audioStates[peticion.audio_file_id] === 'loading' ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      {audioProgress[peticion.audio_file_id] ? `${audioProgress[peticion.audio_file_id]}%` : 'Cargando...'}
                    </>
                  ) : audioStates[peticion.audio_file_id] === 'error' ? (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Reintentar
                    </>
                  ) : audioStates[peticion.audio_file_id] === 'ready' ? (
                    <>
                      <Play className="w-3 h-3 mr-1" />
                      ▶ Listo
                    </>
                  ) : (
                    <>
                      <Play className="w-3 h-3 mr-1" />
                      Cargar
                    </>
                  )}
                </Button>
                <Button
                  onClick={async (e) => {
                    e.preventDefault();
                    logger.log('⬇️ Descargando archivo...');
                    
                    const token = localStorage.getItem('token');
                    const audioFileId = peticion.audio_file_id;
                    const fileName = (peticion.titulo || peticion.title || 'audio') + '.mp3';
                    
                    // PRIMERO: Intentar obtener del caché local
                    try {
                      const cachedData = await getAudioFromCache(audioFileId);
                      if (cachedData && cachedData.blob) {
                        logger.log('✅ Usando archivo desde caché local');
                        const blobUrl = URL.createObjectURL(cachedData.blob);
                        
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = fileName;
                        a.style.display = 'none';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
                        logger.log('✅ Descarga desde caché iniciada');
                        return;
                      }
                    } catch (cacheError) {
                      logger.log('No hay caché local, descargando del servidor...');
                    }
                    
                    // SEGUNDO: Si no hay caché, descargar del servidor con XMLHttpRequest
                    const downloadWithXHR = () => {
                      return new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        const url = `${API}/audio/${audioFileId}?_t=${Date.now()}`;
                        
                        xhr.open('GET', url, true);
                        xhr.responseType = 'blob';
                        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                        xhr.setRequestHeader('Cache-Control', 'no-cache, no-store');
                        
                        xhr.onload = function() {
                          if (xhr.status === 200) {
                            resolve(xhr.response);
                          } else if (xhr.status === 401) {
                            alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
                            localStorage.removeItem('token');
                            window.location.href = '/';
                            reject(new Error('Sesión expirada'));
                          } else if (xhr.status === 403) {
                            alert('No tienes permiso para acceder a este archivo.');
                            reject(new Error('Sin permisos'));
                          } else if (xhr.status === 404) {
                            reject(new Error('Archivo no encontrado en servidor'));
                          } else {
                            reject(new Error(`HTTP ${xhr.status}`));
                          }
                        };
                        
                        xhr.onerror = function() {
                          reject(new Error('Error de red'));
                        };
                        
                        xhr.ontimeout = function() {
                          reject(new Error('Timeout'));
                        };
                        
                        xhr.timeout = 60000;
                        xhr.send();
                      });
                    };
                    
                    try {
                      logger.log('📥 Descargando del servidor...');
                      const blob = await downloadWithXHR();
                      logger.log('✅ Blob recibido, tamaño:', blob.size, 'bytes');
                      
                      const blobUrl = URL.createObjectURL(blob);
                      
                      const a = document.createElement('a');
                      a.href = blobUrl;
                      a.download = fileName;
                      a.style.display = 'none';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
                      logger.log('✅ Descarga iniciada');
                    } catch (error) {
                      console.error('❌ Error al descargar:', error);
                      
                      if (error.message === 'Archivo no encontrado en servidor') {
                        alert('Este archivo ya no existe en el servidor.\n\nEl archivo fue eliminado. Necesitas volver a subirlo.');
                      } else if (error.message !== 'Sesión expirada' && error.message !== 'Sin permisos') {
                        alert(`Error al descargar: ${error.message}`);
                      }
                    }
                  }}
                  size="sm"
                  className="h-11 px-4 bg-purple-600 hover:bg-purple-700 text-white"
                  title="Descargar archivo"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Descargar
                </Button>
              </>
            )}
            
            {/* Barra de progreso + CUE points (Fase 1+2) */}
            {peticion.audio_file_id && (
              <div className="w-full">
                <PeticionAudioProgress
                  peticion={peticion}
                  playlistId={playlistId}
                  isPlaying={playingAudioId === peticion.audio_file_id}
                  setPlayingAudioId={setPlayingAudioId}
                  onUpdated={onPlaylistRefresh}
                />
              </div>
            )}
            
            {/* Botón Descargar MP3 para YouTube sin audio */}
            {(peticion.tipo === 'youtube_link' || peticion.source === 'youtube_link') && !peticion.audio_file_id && peticion.youtube_url && (
              <Button
                onClick={async (e) => {
                  e.preventDefault();
                  const toastId = toast.loading('Descargando MP3 de YouTube... (puede tardar hasta 3 min)');
                  try {
                    const token = localStorage.getItem('token');
                    // 1) Start async job (evita timeout Cloudflare 524)
                    const startRes = await axios.post(
                      `${API}/peticiones/${peticion.id}/download-mp3/start`,
                      null,
                      {
                        params: { playlist_id: playlistId },
                        headers: { 'Authorization': `Bearer ${token}` },
                        timeout: 20000
                      }
                    );
                    const jobId = startRes.data?.job_id;
                    if (!jobId) throw new Error('No se recibió job_id');
                    // 2) Poll cada 3s, hasta 4 min
                    const maxAttempts = 80;
                    let finalRes = null;
                    for (let i = 0; i < maxAttempts; i++) {
                      await new Promise(r => setTimeout(r, 3000));
                      try {
                        const st = await axios.get(
                          `${API}/peticiones/${peticion.id}/download-mp3/status/${jobId}`,
                          { headers: { 'Authorization': `Bearer ${token}` }, timeout: 20000 }
                        );
                        const s = st.data?.status;
                        if (s === 'done') { finalRes = st.data.result; break; }
                        if (s === 'failed') { throw new Error(st.data.error || 'La descarga falló'); }
                      } catch (pollErr) {
                        // Errores transitorios: seguimos hasta timeout. 404 aborta.
                        if (pollErr.response?.status === 404) throw pollErr;
                        if (pollErr.message && !pollErr.response) continue;
                        throw pollErr;
                      }
                    }
                    if (!finalRes) throw new Error('Tiempo de espera agotado (4 min)');
                    if (finalRes.success) {
                      toast.success(`MP3 descargado (${finalRes.file_size_mb} MB)`, { id: toastId });
                      if (onPlaylistRefresh) onPlaylistRefresh();
                    } else if (finalRes.youtube_blocked) {
                      toast.error(finalRes.message || 'YouTube bloquea la descarga desde el servidor', { id: toastId });
                    } else {
                      toast.error(finalRes.message || 'Error al descargar MP3', { id: toastId });
                    }
                  } catch (err) {
                    const msg = err.response?.data?.detail || err.message || 'Error al descargar MP3';
                    console.error('Download MP3 error:', msg, err);
                    toast.error(msg, { id: toastId });
                  }
                }}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white"
                title="Descargar audio MP3 del video de YouTube"
                data-testid={`download-mp3-btn-${peticion.id}`}
              >
                <Download className="w-3 h-3 mr-1" />
                Descargar MP3
              </Button>
            )}

            {/* Botón Buscar en YT y descargar (peticiones sin youtube_url y sin audio) */}
            {!peticion.audio_file_id && !peticion.youtube_url && (peticion.titulo || peticion.title || peticion.spotify_id) && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setYtCandidatesOpen(true);
                }}
                size="sm"
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                title="Buscar en YouTube y elegir versión antes de descargar"
                data-testid={`resolve-download-btn-${peticion.id}`}
              >
                <Download className="w-3 h-3 mr-1" />
                Buscar en YT
              </Button>
            )}

            {/* Botón "Cambiar versión" — para peticiones YA descargadas cuando
                el pick automático fue incorrecto. Reemplaza el MP3 por otro
                elegido a mano de los 3 mejores candidatos de YouTube. */}
            {peticion.audio_file_id && (peticion.titulo || peticion.title || peticion.spotify_id) && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setYtChangeVersionOpen(true);
                }}
                size="sm"
                variant="outline"
                className="h-8 px-2 border-slate-600 text-slate-400 hover:text-cyan-300 hover:bg-cyan-900/20 text-xs"
                title="Cambiar versión: elige otro vídeo de YouTube y reemplaza el MP3 actual"
                data-testid={`change-yt-version-btn-${peticion.id}`}
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
            )}

            {/* Botón Spotify — solo cuando hay link directo (no aparece si es
                fallback de búsqueda). En "Peticiones" el DJ ya tiene MP3/YT y
                el fallback no aporta valor; sí aparece en "Peticiones en Vivo". */}
            {(() => {
              const spResolved = resolveSpotifyUrl(peticion);
              if (!spResolved || spResolved.isSearch) return null;
              return (
                <Button
                  onClick={() => {
                    haptic(10);
                    openExternalLink(spResolved.url);
                  }}
                  size="sm"
                  className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white"
                  title="Abrir en Spotify"
                  data-testid={`spotify-play-btn-${peticion.id}`}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Spotify
                </Button>
              );
            })()}
            
            {/* Botón Añadir a Cola de Spotify — separado del anterior con ml-3 para evitar mispress */}
            {peticion.spotify_uri && (
              <Button
                onClick={() => {
                  haptic(10);
                  if (window.addToSpotifyQueue) {
                    window.addToSpotifyQueue(peticion.spotify_uri, peticion.title, peticion.artist);
                  }
                }}
                size="sm"
                className="h-11 px-4 ml-3 bg-purple-600 hover:bg-purple-700 text-white"
                title="Añadir a cola de Spotify"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Cola
              </Button>
            )}
            
            {/* Checkbox para marcar como pendiente */}
            {peticion.source === 'spotify' && !peticion.song_id && (
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={peticionesPendingFlags[peticion.id] || false}
                  onChange={(e) => {
                    setPeticionesPendingFlags({
                      ...peticionesPendingFlags,
                      [peticion.id]: e.target.checked
                    });
                  }}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-orange-400">🔶 Marcar como pendiente</span>
              </label>
            )}
            
            {/* Botón "A Biblioteca" — Feb-2026: ahora se muestra para CUALQUIER
                petición que tenga identificador (spotify / youtube / mp3 descargado)
                y no exista aún en biblioteca. Dedup por spotify_id (más fiable
                que título+artista). Promueve la petición: hereda spotify_id/url,
                momento del evento y nota original. */}
            {!linkedSong && !peticion.song_id && (
              peticion.spotify_id || peticion.spotify_url || peticion.spotify_uri ||
              peticion.audio_file_id || peticion.youtube_url
            ) && (
              <Button
                onClick={async () => {
                  try {
                    // Verificar si está marcado como pendiente
                    const isPending = peticionesPendingFlags[peticion.id] || false;

                    const pTitle = peticion.title || peticion.titulo;
                    const pArtist = peticion.artist || peticion.artista || '';

                    // Promover: hereda identificadores y momento del evento
                    const newSong = {
                      title: pTitle,
                      artist: pArtist,
                      spotify_id: peticion.spotify_id,
                      spotify_uri: peticion.spotify_uri,
                      spotify_url: peticion.spotify_url,
                      genres: [],
                      rating: null,
                      momentos: peticion.momento ? [peticion.momento] : [],
                      notas: `Promovida desde petición de evento${peticion.nota ? `: ${peticion.nota}` : ''}`,
                      pendiente_denon: isPending
                    };

                    logger.log('🔍 Promoting peticion to library:', { spotify_id: peticion.spotify_id, isPending });

                    await createSong(newSong);

                    // Limpiar el flag después de añadir
                    if (isPending) {
                      const updatedFlags = { ...peticionesPendingFlags };
                      delete updatedFlags[peticion.id];
                      setPeticionesPendingFlags(updatedFlags);
                    }

                    if (isPending) {
                      toast.success('✅ Añadida a biblioteca y marcada como PENDIENTE 🔶');
                    } else {
                      toast.success('✅ Añadida a biblioteca permanente');
                    }
                  } catch (error) {
                    console.error('Error adding to library:', error);
                    const detail = error?.response?.data?.detail;
                    toast.error(detail ? `Error: ${detail}` : 'Error al añadir a biblioteca');
                  }
                }}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-2 text-xs sm:text-sm sm:px-3"
                title="Guardar en biblioteca permanente"
                data-testid={`promote-to-library-btn-${peticion.id}`}
              >
                <Plus className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">A Biblioteca</span>
              </Button>
            )}
          </div>
        </div>
        
        {/* Botones de acción — gap-3 para separar Editar y Eliminar (evita mispress) */}
        <div className="flex flex-col gap-3">
          {/* Botón ocultar/mostrar al cliente */}
          <Button
            onClick={() => onToggleHidden && onToggleHidden(index)}
            size="sm"
            variant="ghost"
            className={`min-h-11 min-w-11 p-2 ${peticion.hidden_from_client 
              ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-900/20' 
              : 'text-slate-400 hover:text-slate-300 hover:bg-slate-700/20'}`}
            title={peticion.hidden_from_client ? "Mostrar al cliente" : "Ocultar al cliente (sorpresa)"}
          >
            {peticion.hidden_from_client ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
          
          {/* Botón editar — abre modal unificado (título/artista/nota/YouTube/Spotify + audio) */}
          {peticion.id && (
            <Button
              onClick={() => setEditDialogOpen(true)}
              size="sm"
              variant="ghost"
              className="min-h-11 min-w-11 p-2 text-sky-400 hover:text-sky-300 hover:bg-sky-900/20"
              title="Editar petición"
              data-testid={`peticion-edit-btn-${peticion.id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
          
          {/* Botón eliminar — pide confirmación (evita borrados accidentales) */}
          <Button
            onClick={() => setConfirmDeleteOpen(true)}
            size="sm"
            variant="ghost"
            className="min-h-11 min-w-11 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"
            title="Eliminar petición"
            data-testid={`peticion-delete-btn-${peticion.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
        </div>
      )}
    </Draggable>
      {/* Modal de edición de petición (título/artista/nota/URLs/audio) */}
      {peticion.id && (
        <PeticionEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          peticion={peticion}
          playlistId={playlistId}
          api={API}
          onSaved={onPlaylistRefresh}
        />
      )}
      {/* Modal de selección de candidatos YouTube */}
      {peticion.id && (
        <YouTubeCandidatesDialog
          open={ytCandidatesOpen}
          onOpenChange={setYtCandidatesOpen}
          peticion={peticion}
          playlistId={playlistId}
          onSuccess={() => {
            if (onPlaylistRefresh) onPlaylistRefresh();
          }}
        />
      )}
      {/* Modal "Cambiar versión" — para peticiones ya descargadas cuando el pick
          automático fue incorrecto. force=true reemplaza el MP3 actual. */}
      {peticion.id && (
        <YouTubeCandidatesDialog
          open={ytChangeVersionOpen}
          onOpenChange={setYtChangeVersionOpen}
          peticion={peticion}
          playlistId={playlistId}
          force={true}
          onSuccess={() => {
            if (onPlaylistRefresh) onPlaylistRefresh();
          }}
        />
      )}
      {/* Confirmación de borrado de petición */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent className="bg-slate-800 border-slate-700 text-white" data-testid="peticion-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar esta petición?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Vas a eliminar «<span className="text-white font-semibold">{peticion.title || peticion.titulo}</span>
              {(peticion.artist || peticion.artista) ? ` — ${peticion.artist || peticion.artista}` : ''}».
              {peticion.audio_file_id ? ' Perderás también su MP3 descargado y los cues.' : ''} Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600 hover:text-white"
              data-testid="peticion-delete-cancel"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmDeleteOpen(false); if (onRemove) onRemove(); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="peticion-delete-confirm"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export { SortablePeticionItem };
export default SortablePeticionItem;
